
import { appPath, appUrl } from '../../core/config/app-base';

export function csvCell(value: unknown): string {
    if (value === null || value === undefined) return '';

    const raw = typeof value === 'string' ? value : String(value);
    const trimmed = raw.trim();

    const needsFormulaEscape =
        typeof value === 'string' &&
        trimmed.length > 0 &&
        ['=', '+', '-', '@'].includes(trimmed[0]);

    const safe = needsFormulaEscape ? `'${raw}` : raw;
    const escaped = safe.replace(/\"/g, '""');
    if (/[\",\r\n]/.test(escaped)) return `"${escaped}"`;
    return escaped;
}

export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function downloadCsv(filename: string, header: string[], rows: unknown[][]): void {
    const csvLines: string[] = [];
    csvLines.push(header.map((h) => csvCell(h)).join(','));
    rows.forEach((row) => csvLines.push(row.map((v) => csvCell(v)).join(',')));
    const csv = `\uFEFF${csvLines.join('\r\n')}`;
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename);
}

function spreadsheetCell(value: unknown): string | number | boolean | Date | null {
    if (value === null || value === undefined) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'number' || typeof value === 'boolean') return value;

    return typeof value === 'string' ? value : String(value);
}

export async function downloadXlsx(
    filename: string,
    sheetName: string,
    header: string[],
    rows: unknown[][]
): Promise<void> {
    const XLSX: any = await import('xlsx');
    const data = [header.map((h) => spreadsheetCell(h)), ...rows.map((r) => r.map((c) => spreadsheetCell(c)))];

    const ws = XLSX.utils.aoa_to_sheet(data);
    const urlPattern = /^https?:\/\/\S+$/i;
    for (const key of Object.keys(ws)) {
      if (key.startsWith('!')) continue;
      const cell = ws[key];
      if (!cell) continue;
      // Financial number format: thousands separators, no decimal points.
      if (typeof cell.v === 'number') {
        cell.z = '#,##0';
        continue;
      }
      if (typeof cell.v !== 'string') continue;
      const value = String(cell.v || '').trim();
      if (!value) continue;
      if (!urlPattern.test(value)) continue;
      cell.l = { Target: value };
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName || 'Sheet1');

    const arrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    downloadBlob(
        new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        filename
    );
}

export function buildPropertyLink(
    propertyId: string | null | undefined,
    mode: 'public' | 'developer' = 'public'
): string {
    const id = String(propertyId || '').trim();
    if (!id) return '';
    const route =
        mode === 'developer'
            ? `/developer/properties/view/${encodeURIComponent(id)}`
            : `/properties/${encodeURIComponent(id)}`;
    return appUrl(route) || appPath(route);
}

export function prependRowNumbers(
    header: string[],
    rows: unknown[][],
    label = '#'
): { header: string[]; rows: unknown[][] } {
    return {
        header: [label, ...header],
        rows: rows.map((row, index) => [index + 1, ...row])
    };
}
